import { create } from 'zustand';
import { endpoints } from '../config/api';

export interface Site {
  id: string;
  name: string;
  code: string;
  address?: string;
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  area_sqft?: number;
}

export interface Building {
  id: string;
  site_id: string;
  name: string;
  code: string;
  building_type?: string;
  floor_area_sqft?: number;
  num_floors: number;
  height_ft?: number;
  smplr_space_id?: string;
}

export interface BuildingModel {
  version_id: string;
  version_number: number;
  gltf_url: string;
  ifc_url: string;
  tiles_url: string;
  created_at: string;
}

interface SitesState {
  sites: Site[];
  selectedSite: Site | null;
  buildings: Building[];
  selectedBuilding: Building | null;
  buildingModel: BuildingModel | null;
  loading: boolean;
  error: string | null;

  fetchSites: () => Promise<void>;
  selectSite: (site: Site) => Promise<void>;
  selectBuilding: (building: Building) => Promise<void>;
}

export const useSitesStore = create<SitesState>((set) => ({
  sites: [],
  selectedSite: null,
  buildings: [],
  selectedBuilding: null,
  buildingModel: null,
  loading: false,
  error: null,

  fetchSites: async () => {
    set({ loading: true, error: null });
    try {
      const response = await fetch(endpoints.sites, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch sites');
      const sites: Site[] = await response.json();

      // Sort sites: PA first, then alphabetically by code
      sites.sort((a, b) => {
        if (a.code === 'PA') return -1;
        if (b.code === 'PA') return 1;
        return a.code.localeCompare(b.code);
      });

      set({ sites, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  selectSite: async (site: Site) => {
    set({ selectedSite: site, selectedBuilding: null, buildingModel: null, loading: true });
    try {
      const response = await fetch(endpoints.buildings(site.id), { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch buildings');
      const buildings = await response.json();
      set({ buildings, loading: false });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  selectBuilding: async (building: Building) => {
    set({ selectedBuilding: building, loading: true });
    try {
      const response = await fetch(endpoints.buildingModel(building.id), { credentials: 'include' });
      if (response.ok) {
        const model = await response.json();
        set({ buildingModel: model, loading: false });
      } else {
        // No model available yet
        set({ buildingModel: null, loading: false });
      }
    } catch (error) {
      set({ buildingModel: null, loading: false });
    }
  },
}));
